import type { VariantType } from '../../../types/variant';
import { TERM_CATEGORY, type TermCategory } from '../types';

/**
 * 連続表示の順序。規約 → プライバシーポリシー → その他（OEM_TERM_CATEGORY の定義順）。
 * API のレスポンス順に依存せず、この順に並べ替えて表示する。
 */
export const RECONSENT_DISPLAY_ORDER: TermCategory[] = [
  TERM_CATEGORY.term,
  TERM_CATEGORY.pp,
  TERM_CATEGORY.tradelaw,
  TERM_CATEGORY.paymentlaw,
  TERM_CATEGORY.oemSubscriptionTerm,
  TERM_CATEGORY.artistSubscriptionTerm,
];

export type ReconsentCopy = {
  title: string;
  checkboxLabel: string;
  /** リード文に埋め込む文書名。「〇〇が改定されました。」の〇〇にあたる */
  documentName: string;
  /** 文書ページへのパス。リード文の文書名を別タブリンクにする。該当ページがない場合は未定義 */
  href?: string;
};

/**
 * 規約タイプごとの文言。
 * 文書名は各 OEM の呼称に合わせて上書きできるよう、呼び出し側から差し替え可能にしている。
 */
export const RECONSENT_COPY: Record<TermCategory, ReconsentCopy> = {
  [TERM_CATEGORY.term]: {
    title: '利用規約改定のお知らせ',
    checkboxLabel: '利用規約に同意する',
    documentName: '利用規約',
    href: '/terms',
  },
  [TERM_CATEGORY.pp]: {
    title: '「個人情報の取扱いについて」改定のお知らせ',
    checkboxLabel: '「個人情報の取扱いについて」に同意する',
    documentName: '個人情報の取扱いについて',
    href: '/policy',
  },
  [TERM_CATEGORY.tradelaw]: {
    title: '特定商取引法に基づく表記の改定のお知らせ',
    checkboxLabel: '特定商取引法に基づく表記に同意する',
    documentName: '特定商取引法に基づく表記',
    href: '/tokusho',
  },
  [TERM_CATEGORY.paymentlaw]: {
    title: '資金決済法に基づく表記の改定のお知らせ',
    checkboxLabel: '資金決済法に基づく表記に同意する',
    // リンクテキストは遷移先ページの見出し（「資金決済法に基づく表記」）に合わせる
    documentName: '資金決済法に基づく表記',
    href: '/shikin',
  },
  // 有料会員規約・ファンクラブ規約は該当ページを持つ OEM がないためリンクを持たない
  [TERM_CATEGORY.oemSubscriptionTerm]: {
    title: '有料会員規約改定のお知らせ',
    checkboxLabel: '有料会員規約に同意する',
    documentName: '有料会員規約',
  },
  [TERM_CATEGORY.artistSubscriptionTerm]: {
    title: 'ファンクラブ規約改定のお知らせ',
    checkboxLabel: 'ファンクラブ規約に同意する',
    documentName: 'ファンクラブ規約',
  },
};

/**
 * 未知の規約タイプ用のフォールバック文言。
 * API 側に新しいカテゴリが追加されてもフロントが落ちないようにするための保険。
 */
export const RECONSENT_COPY_FALLBACK: ReconsentCopy = {
  title: '規約改定のお知らせ',
  checkboxLabel: '内容に同意する',
  documentName: '規約',
};

/**
 * リード文の冒頭で呼びかける OEM 名。
 * 値は各アプリの `apps/{app}/src/constants/index.ts` の `PRODUCT_NAME` と一致させる。
 * 共通パッケージからアプリ側を参照する逆依存を避けるため、ここに持つ。
 */
export const RECONSENT_OEM_NAME: Record<VariantType, string> = {
  // variant 未指定時のデフォルト。共通の再承諾フローを使うのは現状 RAFFLE のみ
  default: 'RAFFLE',
  raffle: 'RAFFLE',
  fanychance: 'FANY Chance',
  junonkuji: 'JUNON TVくじ',
  goodsmilekuji: 'グッスマくじオンライン',
  k2c: 'K2C LUCK',
};

/**
 * リード文の冒頭。OEM 名を挟み込むため関数で持つ。
 * 定義にない variant がキャストで渡された場合は RAFFLE 名にフォールバックする。
 */
export const buildReconsentLeadTextPrefix = (variant: VariantType = 'raffle'): string =>
  `いつも「${RECONSENT_OEM_NAME[variant] ?? RECONSENT_OEM_NAME.raffle}」をご利用いただきありがとうございます。`;

/** リード文の文書名の直後に続く文 */
export const RECONSENT_LEAD_TEXT_REVISED = 'が改定されました。';

/** リード文の末尾。同意が必要な理由を説明する */
export const RECONSENT_LEAD_TEXT_SUFFIX =
  '引き続きサービスをご利用いただくには、下記の内容をご確認の上、同意していただく必要があります。';

/**
 * GA イベント名。
 * 同意率の参考把握のみを目的とした軽量な実装で、管理画面の集計は作らない。
 */
export const RECONSENT_GA_EVENT = {
  /** 同意ボタン押下 */
  agree: 'reconsent_agree',
  /** キャンセル押下（拒否は DB 非記録のため GA でのみ把握する） */
  cancel: 'reconsent_cancel',
} as const;
